

import { useEffect, useState, useRef, useMemo } from "react";
import {
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Grid,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import {
  addField,
  updateField,
  moveFieldDown,
  moveFieldUp,
  deleteField as deleteFieldAction,
  setFields as setFieldsAction,
  saveAllFields,
  getFormInputList,
} from "../../../store/formbuilderSlice";
import FieldEditor from "./FieldEditor";
import DynamicFormRenderer from "./DynamicFormRenderer";
import "./FormBuilder.scss";
import DeleteModal from "../../../components/DeleteModal/DeleteModal";
import Loader from "../../../components/Loader/Loader";
import { toast } from "react-hot-toast";
import CustomToaster from "../../../components/Toaster";
import { TOAST_TIME, TOAST_TITLE, TOAST_TYPE } from "../../../constants/constants";
import { ROLE_TYPES } from "../../../utils/utils";

const makeTempId = () =>
  `temp_${Date.now().toString(36)}_${Math.floor(Math.random() * 10000)}`;

const isEqual = (a, b) => JSON.stringify(a) === JSON.stringify(b);

const OPTION_TYPES = [
  "RADIO_OPTIONS",
  "SELECT",
  "CHECKBOX_OPTIONS",
  "DROPDOWN_MULTI_SELECT",
  "DROPDOWN_SINGLE_SELECT",
];
const TYPES_WITH_NO_OPTIONS = new Set([
  "PROJECT_NAME",
  "PERFORMANCE_ASSESSED_BY",
]);
const cleanPayload = (obj) => {
  const newObj = { ...obj };
  Object.keys(newObj).forEach((key) => {
    const value = newObj[key];
    if (value === "" || value === undefined || (Array.isArray(value) && value.length === 0)) {
      newObj[key] = null;
    }
  });
  return newObj;
};
const normalizeDependencies = (f) => {
  const ids = Array.isArray(f.dependentOnIds) ? f.dependentOnIds.filter(Boolean) : [];
  const hasDependency = ids.length > 0;

  return {
    dependentOnIds: hasDependency ? ids : null,
    dependentValue: hasDependency ? f.dependentValue : null,
    dependentCondition: hasDependency ? f.dependentCondition : null,
  };
};
const finalizeField = (fieldData) => {
  const cleaned = cleanPayload(fieldData);
  if (TYPES_WITH_NO_OPTIONS.has(cleaned.type)) {
    delete cleaned.options;
  }
  return cleaned;
};

const META_OPTIONS = ["scaleCheckBoxEnabled"];

const stripMetaOptions = (options = []) =>
  options.filter(o => !META_OPTIONS.includes(o));

const FormBuilder = ({ formData, onCloseFormInput, isSPOC }) => {
  const dispatch = useDispatch();
  const { fields, loading } = useSelector((state) => state.formBuilder);
  const security = useSelector((state) => state?.security);
  const roles = security?.roles;


  const [open, setOpen] = useState(false);
  const [editingFieldKey, setEditingFieldKey] = useState(null);
  const [localDraft, setLocalDraft] = useState(null);
  const originalRef = useRef(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deletedID, setDeletedID] = useState(null);
  const [deleteIds, setDeleteIds] = useState([]);
  const [validationErrors, setValidationErrors] = useState({});
  const [isOptionError, setIsOptionError] = useState(false);
  const [IsDisabled, setIsDisabled] = useState(false);
  const handleMoveUp = (id) => dispatch(moveFieldUp(id));
  const handleMoveDown = (id) => dispatch(moveFieldDown(id));
  const isHR = ROLE_TYPES.isHr(roles);
  const isReadOnly = isSPOC || IsDisabled;
  useEffect(() => {
    if (formData?.fields && Array.isArray(formData.fields)) {
      const normalized = formData.fields.map((f) => {

        if (f.id) return { ...f };
        return { ...f, tempId: f.tempId || makeTempId() };
      });
      dispatch(setFieldsAction(normalized));

    }
    const responseCount = formData?.responseStats?.total;
    const disabled = responseCount >= 1;
    setIsDisabled(!isHR || (isHR && disabled))
  }, [formData?.fields, dispatch]);
  useEffect(() => {
    fetchAllFields()
  }, [])


  const fetchAllFields = () => {
    dispatch(getFormInputList(formData.id))
  }

  const openAddModal = () => {
    const draft = {
      tempId: makeTempId(),
      label: "",
      description: "",
      type: "TEXT",
      required: true,
      options: [],
      defaultValue: null,
      minValue: "",
      maxValue: "",
      dependentOnIds: [],
      dependentValue: "",
      dependentCondition: null,
    };
    originalRef.current = draft;
    setLocalDraft(draft);
    setEditingFieldKey(null);
    setValidationErrors({});
    setOpen(true);
  };

  const openEditModal = (field) => {
    const key = field.id ?? field.tempId;

    const rawOptions = Array.isArray(field.options) ? field.options : [];
    const draft = {
      ...field,
      dependentOnIds: field.dependentOnIds || [],
      dependentValue: field.dependentValue || "",
      dependentCondition: field.dependentCondition || null,
      options: stripMetaOptions(rawOptions),
      __meta: {
        scaleCheckBoxEnabled: rawOptions.includes("scaleCheckBoxEnabled"),
      },
    };

    originalRef.current = JSON.parse(JSON.stringify(draft));
    setLocalDraft(draft);
    setEditingFieldKey(key);
    setValidationErrors({});
    setOpen(true);
  };

  const closeModal = () => {
    setOpen(false);
    setLocalDraft(null);
    setEditingFieldKey(null);
    originalRef.current = null;
    setValidationErrors({});
    setIsOptionError(false);
  };

  const handleModalSave = () => {
    const errors = validateField(localDraft);
    if (Object.keys(errors).length) {
      setValidationErrors(errors);
      return;
    }
    if (editingFieldKey) {
      dispatch(updateField({ id: editingFieldKey, data: localDraft }));
    } else {
      dispatch(addField(localDraft));
    }
    closeModal();
  };

  const handleDeleteModalopen = (id) => {
    setDeletedID(id);
    setDeleteModalOpen(true);
  };

  const handleDelete = () => {
    const idOrTemp = deletedID;
    const field = fields.find((f) => f.id === idOrTemp || f.tempId === idOrTemp);
    if (field) {
      if (field.id) {
        setDeleteIds((prev) => (prev.includes(field.id) ? prev : [...prev, field.id]));
      }
      dispatch(deleteFieldAction(idOrTemp));
    }
    setDeleteModalOpen(false);
    setDeletedID(null);
  };

  const handleDeleteModalClose = () => {
    setDeleteModalOpen(false);
    setDeletedID(null);
  };

  const validateField = (f) => {
    const errs = {};

    const typeError = validateSingleType(f, fields);
    if (typeError) return { type: typeError };

    const labelError = validateLabel(f, fields);
    if (labelError) return { label: labelError };

    const optionError = validateOptions(f);
    if (optionError) return { options: optionError };

    const rangeError = validateRange(f);
    if (rangeError) return rangeError;

    if (f.type === "NUMERIC_TEXT") {
      const defaultError = validateDefaultValue(f);
      if (defaultError) return defaultError;
    }

    const dependencyError = validateDependency(f);
    if (dependencyError) {
      errs.dependency = dependencyError;
      if (dependencyError.includes("Value")) errs.dependentValue = "Required";
      if (dependencyError.includes("Condition")) errs.dependentCondition = "Required";
    }

    return errs;
  };
  const validateSingleType = (f, fields) => {
    const SINGLE_TYPES = ["AVERAGE_SCORE", "PERFORMANCE_ASSESSED_BY", "PROJECT_NAME"];
    if (!SINGLE_TYPES.includes(f?.type)) return null;

    const currentKey = f.id ?? f.tempId;

    const exists = fields.some(item => {
      if (!item?.type) return false;
      const itemKey = item.id ?? item.tempId;
      if (currentKey && itemKey === currentKey) return false;
      return item.type === f.type;
    });

    return exists
      ? `${f.type.replace(/_/g, " ")} can be added only once`
      : null;
  };
  const validateLabel = (f, fields) => {
    const label = (f?.label || "").trim();
    if (!label) return "Label is required";

    const currentKey = f.id ?? f.tempId;

    const duplicate = fields.some(item => {
      if (!item?.label) return false;
      const itemKey = item.id ?? item.tempId;
      if (currentKey && itemKey === currentKey) return false;
      return item.label.trim().toLowerCase() === label.toLowerCase();
    });

    return duplicate ? "Label already exists" : null;
  };
  const hasInvalidOptionErrors = (f) =>
    Array.isArray(f?.optionErrors) &&
    f.optionErrors.some(err => err !== false);

  const isEmptyOption = (o) => {
    if (o == null) return true;
    const value = typeof o === "string" ? o : o.label;
    return !value || value.trim().length === 0;
  };

  const hasDuplicateOptions = (options) => {
    const seen = new Set();
    for (const o of options) {
      if (!o) continue;
      const raw = typeof o === "string" ? o : o.label || "";
      const key = raw.trim().toLowerCase();

      if (seen.has(key)) return true;
      seen.add(key);
    }
    return false;
  };

  const validateOptions = (f) => {
    if (!OPTION_TYPES.includes(f?.type)) return null;
    const options = Array.isArray(f?.options) ? f.options : [];
    if (options.length === 0) return "At least one option is required";
    if (hasInvalidOptionErrors(f)) return "Invalid options";
    if (options.some(isEmptyOption)) return "Options cannot be empty";
    if (hasDuplicateOptions(options)) return "Duplicate option value";
    return null;
  };
  const validateRange = (f) => {
    const isNumeric = f?.type === "NUMERIC_TEXT";
    if (!isNumeric) return null;

    const minVal = f?.minValue;
    const maxVal = f?.maxValue;

    if (minVal === "" || minVal == null) return { range: "Min value is required" };
    if (maxVal === "" || maxVal == null) return { range: "Max value is required" };

    const min = Number(minVal);
    const max = Number(maxVal);

    if (min >= max) return { range: "Min must be less than Max" };

    return null;
  };

  const validateDefaultValue = (f) => {
    const min = f?.minValue;
    const max = f?.maxValue;
    const d = f?.defaultValue;

    const hasMin = min !== "" && min != null;
    const hasMax = max !== "" && max != null;
    const hasDefault = d !== "" && d != null;

    if (!hasMin && !hasMax && !hasDefault) return null;
    if (hasMin && hasMax) {

      if (hasDefault && (d < min || d > max)) {
        return { defaultValue: `Default value must be between ${min} and ${max}` };
      }
      return null;
    }

    if (hasMin && hasDefault && d < min) {
      return { defaultValue: `Default value must be greater than or equal to ${min}` };
    }

    if (hasMax && hasDefault && d > max) {
      return { defaultValue: `Default value must be less than or equal to ${max}` };
    }

    return null;
  }
  const validateDependency = (f) => {
    const isAvgScore = f?.type?.toUpperCase() === "AVERAGE_SCORE";
    const hasDepIds = f.dependentOnIds && f.dependentOnIds.length > 0 && f.dependentOnIds[0] !== "";
    if (isAvgScore) {
      return !hasDepIds ? "Dependencies required for AVERAGE_SCORE" : null;
    }
    if (hasDepIds && !isAvgScore) {
      if (!f.dependentValue && f.dependentValue !== 0) {
        return "Dependent Value is required";
      }
      if (!f.dependentCondition) {
        return "Condition is required";
      }
    }

    return null;
  };




  const isDirty = useMemo(() => {
    if (!localDraft) return false;
    if (!originalRef.current) return true;

    return !isEqual(
      {
        ...originalRef.current,
        options: stripMetaOptions(originalRef.current.options),
        dependentOnIds: originalRef.current.dependentOnIds || "",
        dependentValue: originalRef.current.dependentValue || "",
        dependentCondition: originalRef.current.dependentCondition || null,
      },
      {
        ...localDraft,
        options: stripMetaOptions(localDraft.options),
        dependentOnIds: localDraft.dependentOnIds || "",
        dependentValue: localDraft.dependentValue || "",
        dependentCondition: localDraft.dependentCondition || null,
      }
    );
  }, [localDraft]);



  const hasValidationErrors = useMemo(() => {
    if (!localDraft) return true;
    const errs = validateField(localDraft);
    setValidationErrors(errs);
    return Object.keys(errs).length > 0;
  }, [localDraft, fields]);

  const saveForm = async () => {
    const createOrUpdate = fields.map((f, index) => {
      const rawField = {
        ...(f.id ? { id: f.id } : { tempId: f.tempId }),
        label: f.label,
        type: f.type,
        inputSortOrder: index + 1,
        required: !!f.required,
        description: f.description,
        options: [
          ...(f.__meta?.scaleCheckBoxEnabled ? ["scaleCheckBoxEnabled"] : f.options),
        ],
        defaultValue: f.defaultValue,
        minValue: f.minValue,
        maxValue: f.maxValue,
        ...normalizeDependencies(f),
      };

      return finalizeField(rawField);
    });

    const payload = {
      id: formData?.id,
      createOrUpdate,
      deleteIds,
      updatedAt: new Date().toISOString(),
    };

    try {
      await dispatch(saveAllFields(payload)).unwrap();
      await dispatch(getFormInputList(formData.id)).unwrap();
      setDeleteIds([]);
    } catch (err) {
    }
  };


  return (
    <>
      {loading && <Loader />}
      <Grid container className="form-main-container">
        <Grid item className="header-container" md={12}>
          <Typography variant="font_21_bold" gutterBottom>
            {formData?.name || "New Form"}
          </Typography>
          <div className="back-button">
            <Button variant="outlined" color="secondary" onClick={onCloseFormInput}>
              <Typography variant="outlineBtnLabel"> Back</Typography>
            </Button>
          </div>
        </Grid>

        <Dialog
          open={open}
          onClose={closeModal}
          fullWidth
          maxWidth="sm"
          PaperProps={{ sx: { borderRadius: 2 } }}
        >
          <DialogTitle className="dialog-title">
            <Typography variant="font_16_bold">
              {editingFieldKey ? "Edit Field" : "Add Field"}
            </Typography>
          </DialogTitle>

          <DialogContent dividers>
            {localDraft && (
              <FieldEditor
                field={localDraft}
                fields={fields}
                validationErrors={validationErrors}
                onChange={(next) => {
                  setLocalDraft(next);

                  const optionErrs = validateField(next).options;
                  setIsOptionError(Boolean(optionErrs));
                }}
                handleError={(flag) => {

                  setIsOptionError(Boolean(flag));
                }}
              />
            )}
          </DialogContent>

          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button variant="outlined" color="secondary" onClick={closeModal}>
              <Typography variant="outlineBtnLabel">Cancel</Typography>
            </Button>
            {!isSPOC && (
              <Button
                disabled={!isDirty || hasValidationErrors}
                variant="contained"
                color="secondary"
                onClick={handleModalSave}
              >
                <Typography variant="outlineBtnLabel">Save</Typography>
              </Button>
            )}
          </DialogActions>
        </Dialog>

        <Grid item md={12} className="dynamic-form-body-container">
          <Box className="scroll-area">
            <DynamicFormRenderer
              formFields={fields}
              onEdit={(f) => openEditModal(f)}
              onDelete={(id) => handleDeleteModalopen(id)}
              onMoveUp={handleMoveUp}
              onMoveDown={handleMoveDown}
              disabled={isReadOnly}
              selectedFormType={formData?.type}
            />
          </Box>

          <Box className="action-footer">
            <div className="back-button">
              {" "}
            </div>
            {!isSPOC && (
              <div className="action-button">
                <Button variant="outlined" color="secondary" disabled={IsDisabled} onClick={openAddModal}>
                  <Typography variant="outlineBtnLabel">+ Add Field</Typography>
                </Button>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={saveForm}
                  disabled={IsDisabled}
                >
                  <Typography variant="outlineBtnLabel">Save Form</Typography>
                </Button>
              </div>
            )}
          </Box>
        </Grid>

        {deleteModalOpen && (
          <DeleteModal
            open={deleteModalOpen}
            handleClose={handleDeleteModalClose}
            handleDelete={handleDelete}
          />
        )}
      </Grid>
    </>
  );
};

export default FormBuilder;


